import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  alpha,
  useTheme,
  useMediaQuery,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface MonthPickerProps {
  open: boolean;
  onClose: () => void;
  availableMonths: Date[];
  currentMonth: Date;
  onMonthSelect: (month: Date) => void;
}

const MonthPicker: React.FC<MonthPickerProps> = ({
  open,
  onClose,
  availableMonths,
  currentMonth,
  onMonthSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('default', { month: 'long', year: 'numeric' });
  };

  const formatShortMonth = (date: Date) => {
    return date.toLocaleDateString('default', { month: 'short', year: '2-digit' });
  };

  const isCurrentMonth = (date: Date) => {
    return (
      date.getMonth() === currentMonth.getMonth() &&
      date.getFullYear() === currentMonth.getFullYear()
    );
  };

  const isThisMonth = (date: Date) => {
    const today = new Date();
    return (
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const handleMonthClick = (month: Date) => {
    onMonthSelect(month);
    onClose();
  };

  // Sort months in reverse chronological order (newest first)
  const sortedMonths = [...availableMonths].sort((a, b) => b.getTime() - a.getTime());

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '80vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)}, ${alpha(theme.palette.background.paper, 0.85)})`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.04)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          borderRadius: '12px 12px 0 0',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            borderRadius: '12px 12px 0 0',
          }
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <CalendarIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Select Month
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 0, pb: 3 }}>
        <Box sx={{ height: 24 }} /> {/* Spacer */}
        {sortedMonths.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            py={4}
            gap={2}
          >
            <CalendarIcon 
              sx={{ 
                fontSize: 64, 
                color: theme.palette.grey[400] 
              }} 
            />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              No budget months available yet.
              <br />
              Create your first budget to get started!
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {sortedMonths.map((month, index) => (
              <Grid item xs={6} sm={4} md={3} key={month.getTime()}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Card
                    onClick={() => handleMonthClick(month)}
                    sx={{
                      cursor: 'pointer',
                      height: '100%',
                      minHeight: isMobile ? 80 : 100,
                      background: isCurrentMonth(month)
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.15)}, ${alpha(theme.palette.primary.light, 0.1)})`
                        : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.paper, 0.4)})`,
                      border: isCurrentMonth(month)
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      position: 'relative',
                      overflow: 'hidden',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                      },
                      ...(isCurrentMonth(month) && {
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        }
                      })
                    }}
                  >
                    <CardContent 
                      sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        gap: 0.5
                      }}
                    >
                      <Typography 
                        variant={isMobile ? "body2" : "subtitle1"} 
                        fontWeight={isCurrentMonth(month) ? 600 : 500}
                        color={isCurrentMonth(month) ? 'primary.main' : 'text.primary'}
                        sx={{ 
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          lineHeight: 1.2
                        }}
                      >
                        {isMobile ? formatShortMonth(month) : formatMonth(month)}
                      </Typography>
                      
                      <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                        {isCurrentMonth(month) && (
                          <Chip
                            label="Current"
                            size="small"
                            color="primary"
                            variant="filled"
                            sx={{ 
                              height: { xs: 18, sm: 20 },
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              fontWeight: 500
                            }}
                          />
                        )}
                        
                        {isThisMonth(month) && !isCurrentMonth(month) && (
                          <Chip
                            label="This Month"
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ 
                              height: { xs: 18, sm: 20 },
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              fontWeight: 500
                            }}
                          />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MonthPicker; 